<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  threads: {
    type: Array,
    default: () => [],
  },
  selectedThreadId: {
    type: [Number, String],
    default: null,
  },
  hasMore: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'close',
  'selectThread',
  'newChat',
  'threadDeleted',
  'loadMore',
  'deleteAll',
]);

const { t } = useI18n();
const store = useStore();

const deleteDialogRef = ref(null);
const deleteAllDialogRef = ref(null);
const openDropdownId = ref(null);
const threadToDelete = ref(null);

const sortedThreads = computed(() => {
  if (!props.threads || props.threads.length === 0) return [];
  return [...props.threads].sort((a, b) => {
    const aTime = a.updated_at || a.created_at || 0;
    const bTime = b.updated_at || b.created_at || 0;
    return bTime - aTime;
  });
});

const formatTime = timestamp => {
  if (!timestamp) return '';
  const ms = timestamp > 9999999999 ? timestamp : timestamp * 1000;
  return formatDistanceToNow(new Date(ms), {
    addSuffix: true,
    locale: zhCN,
  });
};

const menuItems = computed(() => [
  {
    label: t('PILOT.COPILOT.DELETE_THREAD'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const selectThread = thread => {
  emit('select-thread', thread.id);
};

const startNewChat = () => {
  emit('new-chat');
};

const close = () => {
  emit('close');
};

const toggleDropdown = threadId => {
  openDropdownId.value = openDropdownId.value === threadId ? null : threadId;
};

const handleAction = (actionObj, threadId) => {
  openDropdownId.value = null;
  if (actionObj.action === 'delete') {
    threadToDelete.value = threadId;
    deleteDialogRef.value?.open();
  }
};

const confirmDelete = async () => {
  const threadId = threadToDelete.value;
  if (!threadId) return;

  try {
    await store.dispatch('pilotCopilotThreads/delete', threadId);
    useAlert(t('PILOT.COPILOT.DELETE_THREAD_SUCCESS'));
    emit('thread-deleted', threadId);
    deleteDialogRef.value?.close();
  } catch (error) {
    useAlert(t('PILOT.COPILOT.DELETE_THREAD_ERROR'));
  } finally {
    threadToDelete.value = null;
  }
};

const openDeleteAllDialog = () => {
  deleteAllDialogRef.value?.open();
};

const confirmDeleteAll = async () => {
  try {
    await store.dispatch('pilotCopilotThreads/deleteAll');
    useAlert(t('PILOT.COPILOT.DELETE_ALL_THREADS_SUCCESS'));
    emit('delete-all');
    deleteAllDialogRef.value?.close();
  } catch (error) {
    useAlert(t('PILOT.COPILOT.DELETE_ALL_THREADS_ERROR'));
  }
};

const loadMore = () => {
  if (!props.loading && props.hasMore) {
    emit('load-more');
  }
};
</script>

<template>
  <!-- 遮罩层 -->
  <Transition name="fade">
    <div
      v-if="isOpen"
      class="absolute inset-0 bg-black/30 z-40"
      @click="close"
    />
  </Transition>

  <!-- 抽屉 -->
  <Transition name="slide">
    <div
      v-if="isOpen"
      class="absolute top-0 left-0 h-full w-[260px] bg-n-surface-2 z-50 flex flex-col shadow-xl border-r border-n-weak"
    >
      <!-- 顶部：Logo + 关闭按钮 -->
      <div class="flex items-center justify-between px-4 py-3">
        <div class="flex items-center gap-2">
          <span class="i-woot-captain text-xl text-n-slate-12" />
        </div>
        <button
          class="p-1 rounded hover:bg-n-slate-3 transition-colors"
          @click="close"
        >
          <span class="i-lucide-x text-n-slate-11 text-base" />
        </button>
      </div>

      <!-- 新对话按钮 -->
      <div class="px-3 pb-2">
        <button
          class="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl hover:bg-n-slate-3 dark:hover:bg-n-slate-3/50 transition-colors text-sm text-n-slate-12"
          @click="startNewChat"
        >
          <span class="i-lucide-edit-3 text-base text-n-slate-11" />
          {{ t('PILOT.COPILOT.NEW_CHAT') }}
        </button>
      </div>

      <!-- 聊天记录标题 -->
      <div class="px-4 py-2 flex items-center justify-between">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('PILOT.COPILOT.CHAT_HISTORY') }}
        </span>
        <button
          v-if="threads.length > 0"
          class="text-xs text-n-slate-10 hover:text-n-red-11 transition-colors flex items-center gap-1"
          @click="openDeleteAllDialog"
        >
          <span class="i-lucide-trash-2 text-xs" />
          {{ t('PILOT.COPILOT.CLEAR_ALL') }}
        </button>
      </div>

      <!-- 线程列表 -->
      <div class="flex-1 overflow-y-auto px-3 space-y-1">
        <button
          v-for="thread in sortedThreads"
          :key="thread.id"
          class="group w-full flex items-center gap-2 px-3 py-2.5 rounded-xl hover:bg-n-slate-3 dark:hover:bg-n-slate-3/50 transition-colors text-left"
          :class="{
            'bg-n-slate-3 dark:bg-n-slate-3/50': selectedThreadId === thread.id,
          }"
          @click="selectThread(thread)"
        >
          <span
            class="i-lucide-message-circle text-n-slate-11 text-base shrink-0"
          />
          <div class="min-w-0 flex-1">
            <div class="text-sm text-n-slate-12 truncate font-medium">
              {{ thread.title || t('PILOT.COPILOT.UNTITLED_CHAT') }}
            </div>
            <div class="text-xs text-n-slate-10">
              {{ formatTime(thread.updated_at || thread.created_at) }}
            </div>
          </div>
          <!-- 更多操作按钮 -->
          <span class="relative shrink-0 flex items-center">
            <button
              class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-n-slate-4 transition-colors opacity-0 group-hover:opacity-100"
              @click.prevent.stop="toggleDropdown(thread.id)"
            >
              <span class="i-lucide-more-horizontal text-n-slate-11 text-2xl" />
            </button>
            <DropdownMenu
              v-if="openDropdownId === thread.id"
              :menu-items="menuItems"
              class="mt-1 ltr:right-0 rtl:left-0 top-full"
              @action="handleAction($event, thread.id)"
            />
          </span>
        </button>

        <!-- 空状态 -->
        <div v-if="threads.length === 0" class="px-3 py-8 text-center">
          <div class="i-lucide-message-square text-3xl text-n-slate-8 mb-2" />
          <div class="text-xs text-n-slate-10">
            {{ t('PILOT.COPILOT.NO_CHAT_HISTORY') }}
          </div>
        </div>

        <!-- 加载更多按钮 -->
        <div v-if="hasMore && threads.length > 0" class="px-3 py-2">
          <button
            class="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg hover:bg-n-slate-3 dark:hover:bg-n-slate-3/50 transition-colors text-sm text-n-slate-11"
            :disabled="loading"
            @click="loadMore"
          >
            <span
              v-if="loading"
              class="i-lucide-loader-2 text-base animate-spin"
            />
            <span v-else class="i-lucide-chevron-down text-base" />
            {{
              loading
                ? t('PILOT.COPILOT.LOADING')
                : t('PILOT.COPILOT.LOAD_MORE')
            }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- 删除确认对话框 -->
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('PILOT.COPILOT.DELETE_THREAD_TITLE')"
    :description="t('PILOT.COPILOT.DELETE_THREAD_CONFIRM')"
    :confirm-button-label="t('PILOT.COPILOT.DELETE_THREAD')"
    @confirm="confirmDelete"
  />

  <!-- 清空所有聊天确认对话框 -->
  <Dialog
    ref="deleteAllDialogRef"
    type="alert"
    :title="t('PILOT.COPILOT.DELETE_ALL_THREADS_TITLE')"
    :description="t('PILOT.COPILOT.DELETE_ALL_THREADS_CONFIRM')"
    :confirm-button-label="t('PILOT.COPILOT.CLEAR_ALL')"
    @confirm="confirmDeleteAll"
  />
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
